import React, { useState, useEffect } from 'react';
import { AlertCircle, RefreshCw, X } from 'lucide-react';
import { Button } from './Button';
import { networkDiagnostics } from '@/lib/utils/network-diagnostics';

export function NetworkErrorBanner() {
  const [isVisible, setIsVisible] = useState(false);
  const [errorDetails, setErrorDetails] = useState<{
    error?: string;
    suggestion?: string;
  }>({});
  const [isChecking, setIsChecking] = useState(false);

  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      if (event.error?.message?.includes('ERR_NAME_NOT_RESOLVED') && 
          event.error?.message?.includes('supabase')) {
        checkConnection();
      }
    };

    window.addEventListener('error', handleError);
    
    const recentError = sessionStorage.getItem('supabase-network-error');
    if (recentError) {
      try {
        const error = JSON.parse(recentError);
        if (Date.now() - error.timestamp < 300000) { // 5 minutes
          setErrorDetails(error);
          setIsVisible(true);
        }
      } catch {
        // Ignore parse errors
      }
    }

    return () => {
      window.removeEventListener('error', handleError);
    };
  }, []);

  const checkConnection = async () => {
    setIsChecking(true);
    const result = await networkDiagnostics.diagnoseAndLog();
    
    if (!result.reachable) {
      const error = {
        error: result.error,
        suggestion: result.suggestion,
        timestamp: Date.now()
      };
      setErrorDetails(error);
      setIsVisible(true);
      sessionStorage.setItem('supabase-network-error', JSON.stringify(error));
    } else {
      setIsVisible(false);
      sessionStorage.removeItem('supabase-network-error');
    }
    
    setIsChecking(false);
  };

  const dismiss = () => {
    setIsVisible(false);
    sessionStorage.removeItem('supabase-network-error');
  };

  if (!isVisible) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-red-900/90 backdrop-blur-sm border-b border-red-700 p-3 sm:p-4">
      <div className="max-w-7xl mx-auto flex items-start gap-3">
        <AlertCircle className="h-5 w-5 text-red-300 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-medium text-red-100">Connection Error</h3>
          <p className="text-xs text-red-200 mt-1">
            Unable to connect to our servers. Your bookings may not appear.
          </p>
          {errorDetails.suggestion && (
            <p className="text-xs text-red-300 mt-2">
              <strong>Try:</strong> {errorDetails.suggestion}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={checkConnection}
            disabled={isChecking}
            className="text-xs border-red-600 text-red-100 hover:bg-red-800"
          >
            {isChecking ? (
              <RefreshCw className="h-3 w-3 animate-spin" />
            ) : (
              <>
                <RefreshCw className="h-3 w-3 mr-1" />
                Retry
              </>
            )}
          </Button>
          <button
            onClick={dismiss}
            className="text-red-300 hover:text-red-100 transition-colors"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
} 
